import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { JButton } from "@/components/JButton";
import { toast } from "sonner";
import { z } from "zod";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Forgot password? | Jozori جذوري" },
      { name: "description", content: "Reset your Jozori account password." },
    ],
  }),
  component: ForgotPasswordPage,
});

const emailSchema = z.string().trim().email("Enter a valid email").max(255);

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = emailSchema.safeParse(email);
    if (!r.success) return toast.error(r.error.issues[0].message);
    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    setSent(true);
    toast.success("Check your inbox for the reset link.");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="px-5 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-baseline gap-2">
          <span className="font-display-ar text-2xl font-black text-primary">جذوري</span>
          <span className="font-display-en text-lg font-extrabold text-foreground/40">/</span>
          <span className="font-display-en text-lg font-extrabold tracking-tight">JOZORI</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 pb-10">
        <div className="w-full max-w-md bg-card border-2 border-clay/30 rounded-3xl shadow-2xl p-7 md:p-9">
          <h1 className="font-display-en text-3xl font-bold mb-1 tracking-tight">
            Forgot password?
          </h1>
          <p className="text-sm text-foreground/60 mb-2">
            Enter your email and we'll send a reset link.
          </p>
          <p className="text-sm text-foreground/60 mb-6" dir="rtl">
            أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين.
          </p>

          {sent ? (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-clay/10 border border-clay/30">
                <p className="text-sm text-foreground/80">
                  We sent a reset link to <strong>{email}</strong>. Check your
                  inbox (and spam folder).
                </p>
              </div>
              <JButton onClick={() => navigate({ to: "/login" })} className="w-full" size="lg">
                Back to sign in
              </JButton>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={255}
                className="w-full px-4 py-3 rounded-2xl border-2 border-clay/40 bg-background focus:border-primary focus:outline-none"
              />
              <JButton type="submit" disabled={busy} className="w-full" size="lg">
                {busy ? "Sending…" : "Send reset link"}
              </JButton>
              <p className="text-sm text-center mt-5 text-foreground/60">
                Remembered it?{" "}
                <Link to="/login" className="text-primary font-bold hover:underline">
                  Sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
